'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('ImageCropper');

interface ImageCropperProps {
  imageUrl: string;
  aspectRatio?: number;
  isOpen?: boolean;
  onCrop: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function ImageCropper({
  imageUrl,
  aspectRatio,
  isOpen = true,
  onCrop,
  onCancel,
}: ImageCropperProps) {
  const t = useTranslations('imageCrop');
  const tCommon = useTranslations('common');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [initialPinchDistance, setInitialPinchDistance] = useState<
    number | null
  >(null);
  const [initialCropArea, setInitialCropArea] = useState<CropArea | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      setImageError('No image URL provided');
      return;
    }

    const img = new Image();
    // Don't set crossOrigin for blob URLs as they're same-origin

    img.onload = () => {
      if (imageRef.current) {
        imageRef.current.src = img.src;

        // Wait for the image element to be ready
        setTimeout(() => {
          if (!imageRef.current) return;

          // Get the actual displayed dimensions (currently not used)
          // const displayWidth = imageRef.current.width;
          // const displayHeight = imageRef.current.height;

          // Calculate scale factor (currently not used but may be needed for future enhancements)
          // const scaleX = displayWidth / img.width;
          // const scaleY = displayHeight / img.height;
          // const scale = Math.min(scaleX, scaleY);

          // Set initial crop area to center of image (in original image coordinates)
          const initialSize = Math.min(img.width, img.height) * 0.5;
          const initialX = (img.width - initialSize) / 2;
          const initialY = (img.height - initialSize) / 2;

          setCropArea({
            x: initialX,
            y: initialY,
            width: initialSize,
            height: aspectRatio ? initialSize / aspectRatio : initialSize,
          });

          setImageLoaded(true);
          setImageError(null);
        }, 100);
      }
    };

    img.onerror = (e) => {
      console.error('Failed to load image:', e);
      setImageError('Failed to load image. Please try again.');
      setImageLoaded(false);
    };

    // Set the source after setting up handlers
    img.src = imageUrl;

    // Cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, aspectRatio]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = imageRef.current?.getBoundingClientRect();
      if (rect) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - rect.left - cropArea.x,
          y: e.clientY - rect.top - cropArea.y,
        });
      }
    },
    [cropArea]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (e.touches.length === 1) {
        // Single touch - start dragging
        const touch = e.touches[0];
        if (touch) {
          setIsDragging(true);
          setDragStart({
            x: touch.clientX - rect.left - cropArea.x,
            y: touch.clientY - rect.top - cropArea.y,
          });
        }
      } else if (e.touches.length === 2) {
        // Two fingers - prepare for pinch zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const distance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );
          setInitialPinchDistance(distance);
          setInitialCropArea({ ...cropArea });
        }
      }
    },
    [cropArea]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !imageRef.current) return;

      const rect = imageRef.current.getBoundingClientRect();
      const img = imageRef.current;

      let newX = e.clientX - rect.left - dragStart.x;
      let newY = e.clientY - rect.top - dragStart.y;

      // Constrain crop area within image bounds
      newX = Math.max(0, Math.min(newX, img.naturalWidth - cropArea.width));
      newY = Math.max(0, Math.min(newY, img.naturalHeight - cropArea.height));

      setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
    },
    [isDragging, dragStart, cropArea.width, cropArea.height]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      const rect = imageRef.current?.getBoundingClientRect();
      const img = imageRef.current;
      if (!rect || !img) return;

      if (e.touches.length === 1 && isDragging) {
        // Single touch - move crop area
        const touch = e.touches[0];
        if (touch) {
          let newX = touch.clientX - rect.left - dragStart.x;
          let newY = touch.clientY - rect.top - dragStart.y;

          // Constrain crop area within image bounds
          newX = Math.max(0, Math.min(newX, img.naturalWidth - cropArea.width));
          newY = Math.max(
            0,
            Math.min(newY, img.naturalHeight - cropArea.height)
          );

          setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
        }
      } else if (
        e.touches.length === 2 &&
        initialPinchDistance &&
        initialCropArea
      ) {
        // Two fingers - pinch to resize crop area
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const currentDistance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );

          const scale = currentDistance / initialPinchDistance;

          // Calculate new size maintaining aspect ratio if specified
          let newWidth = initialCropArea.width * scale;
          let newHeight = initialCropArea.height * scale;

          if (aspectRatio) {
            newHeight = newWidth / aspectRatio;
          }

          // Constrain within image bounds
          newWidth = Math.min(newWidth, img.naturalWidth - initialCropArea.x);
          newHeight = Math.min(
            newHeight,
            img.naturalHeight - initialCropArea.y
          );
          newWidth = Math.max(50, newWidth); // Minimum size
          newHeight = Math.max(50, newHeight);

          setCropArea((prev) => ({
            ...prev,
            width: newWidth,
            height: newHeight,
          }));
        }
      }
    },
    [
      isDragging,
      dragStart,
      cropArea.width,
      cropArea.height,
      initialPinchDistance,
      initialCropArea,
      aspectRatio,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setInitialPinchDistance(null);
    setInitialCropArea(null);
  }, []);

  const handleCrop = useCallback(() => {
    logger.debug('handleCrop called');
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) {
      console.error('Canvas or image not ready');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    logger.debug('Crop area:', cropArea);

    // Set canvas size to crop area size
    canvas.width = cropArea.width;
    canvas.height = cropArea.height;

    // Draw cropped image
    ctx.drawImage(
      image,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      cropArea.width,
      cropArea.height
    );

    logger.debug('Converting canvas to blob...');
    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          logger.info('Blob created, size:', blob.size);
          onCrop(blob);
        } else {
          console.error('Failed to create blob from canvas');
        }
      },
      'image/jpeg',
      0.9
    );
  }, [cropArea, onCrop]);

  if (imageError) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {tCommon('error')}
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{imageError}</AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button onClick={onCancel}>{tCommon('close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!imageLoaded) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">
              {tCommon('loadingImage')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <span>{tCommon('loadingImage')}</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div
          className="relative inline-block cursor-move touch-none"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            ref={imageRef}
            alt="Crop preview"
            className="max-w-full h-auto block"
            style={{
              maxHeight: '60vh',
              display: imageLoaded ? 'block' : 'none',
            }}
            onLoad={() => logger.debug('Image element loaded')}
            onError={(e) => console.error('Image element error:', e)}
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Crop area */}
          <div
            className="absolute border-2 border-white touch-none"
            style={{
              left: `${cropArea.x}px`,
              top: `${cropArea.y}px`,
              width: `${cropArea.width}px`,
              height: `${cropArea.height}px`,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {/* Corner handles */}
            <div className="absolute -top-1 -left-1 w-3 h-3 bg-white border border-muted-foreground" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border border-muted-foreground" />
            <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border border-muted-foreground" />
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border border-muted-foreground" />
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleCrop}>{t('saveButton')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
